<template>
  <div class="room-option-container">
    <div class="wrap-options flex-1">
      <hr class="border border-black-light-2 my-5 lg:hidden md:hidden" />

      <whats-included
        :all-available-transfers="allAvailableTransfers"
      ></whats-included>

      <hr class="border border-black-light-2 my-5 lg:hidden md:hidden" />

      <div>
        <h4 class="fs-16 font-medium mb-4 sm:mb-3">Meal plans</h4>
        <div class="ml-3">
          <div v-for="(item, index) in allAvailableMealPlans" :key="item.code">
            <meal-plan-info
              :style="{
                position: 'relative',
                'z-index': allAvailableMealPlans.length - index,
              }"
              :current-meal-plan="currentMealPlan"
              :room="room"
              :item="item"
              @change="($event) => updateSelectedDeal($event)"
            />
          </div>
        </div>
      </div>
    </div>

    <hr class="border border-black-light-2 my-5 lg:hidden md:hidden" />

    <div
      class="wrap-price flex flex-col justify-center items-center gap-3 sm:items-end sm:gap-4"
    >
      <div class="flex justify-between items-end w-full lg:justify-center">
        <span
          class="discount-tag offer--purplePrimary lg:hidden md:hidden"
          v-if="getDiscountForSelectedRoom(room.code)"
        >
          {{ getDiscountForSelectedRoom(room.code) }}% off
        </span>
        <div class="flex flex-col items-center sm:items-end">
          <span
            class="old-price line-through mb-1"
            v-if="currentDeal.price.discount"
          >
            <currency-context>
              <template v-slot="{ convert }">
                <span
                  v-text="
                    convert(applyRoomMarkup(room.code, currentDeal.price.gross))
                  "
                ></span>
              </template>
            </currency-context>
          </span>
          <span class="font-medium fs-24 mdsm:fs-20">
            <currency-context>
              <template v-slot="{ convert }">
                <span
                  v-text="
                    convert(applyRoomMarkup(room.code, currentDeal.price.nett))
                  "
                ></span>
              </template>
            </currency-context>
          </span>
        </div>
      </div>

      <div class="flex justify-between w-full gap-4 whitespace-no-wrap">
        <button
          :disabled="isSelected"
          @click="selectRoom"
          class="btn btn-secondary w-full py-2"
        >
          {{ selectButtonText }}
        </button>
      </div>

      <div v-if="isLimitedAvailability()" class="text-warning font-medium">
        Limited availability
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from "vuex";
import uniqBy from "lodash/uniqBy";
import orderBy from "lodash/orderBy";
import CurrencyContext from "../../core/CurrencyContext";
import Tooltip from "../../core/Tooltip";
import {
  breakdownMealPlanPrices,
  breakdownTransferPrices,
  breakdownDealComponent,
} from "../../../helpers/hummingbirdHelpers";
import { fullDisplayDate, totalDays } from "../../../helpers";
import MealPlanInfo from "./MealPlanInfo";

export default {
  props: ["room", "group"],
  components: {
    Tooltip,
    CurrencyContext,
    MealPlanInfo,
  },
  data() {
    return {
      selectedDeal: this.room.deals[0].id,
      viewPackages: false,
    };
  },
  mounted() {
    if (this.getSelectedRoomDetails.length) {
      this.getSelectedRoomDetails.forEach((item) => {
        if (item.group === "group-1") {
          this.selectedDeal = item.id;
        }
      });
    }

    if (this.isMobile()) {
      pushUpTrengoChat(true);
    }
  },
  computed: {
    ...mapState([
      "dates",
      "groupsResult",
      "selectedRooms",
      "roomsFilter",
      "bookingRooms",
      "selectedGroupId",
    ]),
    ...mapGetters([
      "applyRoomMarkup",
      "getDiscountForSelectedRoom",
      "getSelectedRoomDetails",
    ]),
    brokendownMealPlanPrices() {
      return breakdownMealPlanPrices(this.room.deals);
    },
    brokendownTransferPrices() {
      return breakdownTransferPrices(this.room.deals);
    },
    allAvailableMealPlans() {
      const allAccomodations = this.room.deals.map(
        (deal) => deal.info.accommodation
      );
      const allMealPlans = allAccomodations.map(
        (accommodation) => accommodation.mealPlan
      );
      const uniqueMealPlans = uniqBy(allMealPlans, (mp) => mp.code);

      for (const mealPlan of uniqueMealPlans) {
        mealPlan.priceDiff =
          this.brokendownMealPlanPrices[this.currentTransfer][mealPlan.code][3];
      }

      return orderBy(uniqueMealPlans, ["priceDiff"], ["asc"]);
    },
    allAvailableTransfers() {
      const allTransfers = this.room.deals.map((deal) => {
        const transfer = deal.info.transfer;
        const { arrival, departure } = transfer;

        return {
          code: `${arrival.code}:${departure.code}`,
          name: arrival.name,
        };
      });

      const uniqueTransfers = uniqBy(allTransfers, (transfer) => transfer.code);
      for (const transfer of uniqueTransfers) {
        transfer.priceDiff =
          this.brokendownTransferPrices[this.currentMealPlan][transfer.code][3];
      }

      const transfersToShow = uniqueTransfers.filter(
        (transfer) => transfer.priceDiff <= 1000
      );

      return transfersToShow;
    },
    currentMealPlan() {
      return this.selectedDealComponents.mealPlanCode;
    },
    currentTransfer() {
      return this.selectedDealComponents.transferCode;
    },
    currentDeal() {
      const theDeal = this.room.deals.find(
        (deal) => deal.id === this.selectedDeal
      );

      if (!theDeal) {
        this.selectedDeal = this.room.deals[0].id;
        return this.room.deals[0];
      }

      return theDeal;
    },
    selectedDealComponents() {
      return breakdownDealComponent(this.selectedDeal);
    },
    cancellationPoliciesText() {
      return this.currentDeal.cancellationPolicies
        .map((policy) => {
          return policy.description.join(", ");
        })
        .filter((policy) => policy)
        .join(" ");
    },
    isSelected() {
      let result = false;

      if (this.selectedRooms.length) {
        this.selectedRooms.forEach((item) => {
          if (item.roomCode === this.room.code && item.group === this.group) {
            result = true;
          }
        });
      }
      return result;
    },
    selectButtonText() {
      if (this.isSelected) {
        return "Current Selection";
      }

      if (this.groupsResult.length <= 1) {
        return "Select room";
      }

      const groupNumber = this.selectedGroupId.split("-")[1];
      return `Select room ${groupNumber}`;
    },
    unselectedGroups() {
      const selectedGroups = this.selectedRooms.map(
        (room) => room.group.split("-")[1]
      );

      let results = [];
      for (let i = 1; i <= this.bookingRooms.length; i++) {
        if (!selectedGroups.includes(i.toString())) {
          results.push(i);
        }
      }
      return results;
    },
  },
  methods: {
    ...mapMutations(["setSelectedDealIdPerGroup"]),
    isLimitedAvailability() {
      let selectedDeal = this.room.deals[0].id;
      const roomTemp = this.selectedRooms.find(
        (_r) =>
          _r.group === this.selectedGroupId && _r.roomCode === this.room.code
      );

      if (roomTemp) {
        selectedDeal = roomTemp.id;
      }

      const currentDeal = this.room.deals.find(
        (deal) => deal.id === selectedDeal
      );
      return currentDeal.info.availability !== "free_sell";
    },
    totalDays(nights = false) {
      const fromDate = this.dates[0];
      const toDate = this.dates[1];

      if (fromDate && toDate) {
        return totalDays(fromDate, toDate, nights);
      } else if (fromDate && !toDate) {
        return totalDays(fromDate, fromDate);
      }

      return "";
    },
    bookingDate() {
      const fromDate = this.dates[0];
      const toDate = this.dates[1];

      return fullDisplayDate(fromDate, toDate);
    },
    updateSelectedDeal({ field, value }) {
      const {
        hotelCode,
        roomCode,
        mealPlanCode,
        arrivalTransferCode,
        departureTransferCode,
      } = this.selectedDealComponents;

      if (field === "mealPlan") {
        this.selectedDeal = `${hotelCode}:${roomCode}:${value}:${arrivalTransferCode}:${departureTransferCode}`;
      } else if (field === "transfer") {
        this.selectedDeal = `${hotelCode}:${roomCode}:${mealPlanCode}:${value}`;
      }

      this.refreshSelectedRooms();
    },
    refreshSelectedRooms() {
      const index = this.selectedRooms.findIndex((room) => {
        return (
          room.id.split(":")[1] === this.selectedDealComponents.roomCode &&
          this.group === room.group
        );
      });

      if (index >= 0) {
        const newSelectedRooms = [...this.selectedRooms];
        newSelectedRooms[index].id = this.selectedDeal;
        this.$store.commit("setSelectedRooms", newSelectedRooms);
        this.$store.dispatch("updateSelectedRoomsParamsUrl");
      }
    },
    getTotalGuests(id) {
      const result = this.bookingRooms.find((room) => room.id === id);
      return {
        adults: result.adults,
        children: result.children.length,
      };
    },
    selectRoom() {
      const roomId = Number(this.group.trim().slice(6));
      const data = {
        group: this.group,
        id: this.currentDeal.id,
        roomCode: this.room.code,
        guests: this.getTotalGuests(roomId),
      };

      let newSelectedRooms = [...this.selectedRooms];
      if (!newSelectedRooms.length) {
        newSelectedRooms = [data];
      } else {
        const index = newSelectedRooms.findIndex(
          (room) => room.group === data.group
        );
        if (index >= 0) {
          newSelectedRooms[index] = data;
        } else {
          newSelectedRooms.push(data);
        }
      }
      this.$store.commit("setSelectedRooms", newSelectedRooms);
      this.$store.dispatch("updateSelectedRoomsParamsUrl");

      const newGroup = this.unselectedGroups[0];
      if (newGroup) {
        this.$store.commit("setSelectedGroupId", `group-${newGroup}`);

        const tab = document.querySelector(
          `.group-title[data-group-id="group-${newGroup}"]`
        );

        window.scrollToElement(
          $(`.group-title[data-group-id="group-${newGroup}"]`)
        );
      }

      this.$nextTick(() => {
        if (this.selectedRooms.length === this.bookingRooms.length) {
          this.$store.commit("setShowPriceDetailsModal", value);
        }
      });
    },
    isMobile() {
      return window.matchMedia("only screen and (max-width: 600px)").matches;
    },
  },
  watch: {
    group(val) {
      const room = this.getSelectedRoomDetails.find(
        (room) => room.group === val && room.roomCode === this.room.code
      );

      if (room) {
        this.selectedDeal = room.id;
      }
    },
    selectedDeal: {
      immediate: true,
      handler(val) {
        this.$store.commit("setSelectedDealIdPerGroup", {
          groupId: this.selectedGroupId,
          dealId: val,
        });
      },
    },
  },
};
</script>

<style scoped>
.wrap-options {
  display: grid;
  column-gap: 3.25rem;
  grid-template-columns: 1.5fr 1fr;
}

.discount-tag {
  padding: 5px 8px;
  border-radius: 4px;
}
.room-option-container {
  gap: 3rem;
  display: flex;
}

.ml-30 {
  margin-left: 30px;
}
.wrap-price {
  margin-top: -46px;
  width: 200px;
}

.old-price {
  line-height: 16px;
  color: var(--black-light-5);
}

.success-color {
  color: var(--success-primary);
}

@media (max-width: 600px) {
  .wrap-options {
    display: flex;
    flex-direction: column;
  }
  .room-option-container {
    gap: 0;
    flex-direction: column;
  }

  .wrap-price {
    margin-top: 0px;
    width: 100%;
  }

  .info-option {
    margin-bottom: 1rem;
  }
}
</style>
